import React, { useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import WorkspacePage from './pages/WorkspacePage';
import BoardPage from './pages/BoardPage';
import TemplatesPage from './pages/TemplatesPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import IntegrationsPage from './pages/IntegrationsPage';
import HelpPage from './pages/HelpPage';
import UpgradePage from './pages/UpgradePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import StrategyCoachPage from './pages/StrategyCoachPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AdminCMSPage from './pages/AdminCMSPage';
import AdminAIPage from './pages/AdminAIPage';
import AdminIntelligencePage from './pages/AdminIntelligencePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FairUsePage from './pages/FairUsePage';
import AnimatedSplashScreen from './components/AnimatedSplashScreen';
import AppLayout from './components/AppLayout';
import DisclaimerModal from './components/DisclaimerModal';

// Google OAuth callback component — receives code from Google, sends to backend
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const { processGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (code) {
      // Close in-app browser on mobile
      if (Capacitor.isNativePlatform()) {
        Browser.close().catch(() => {});
      }
      processGoogleCallback(code)
        .then(() => {
          navigate('/dashboard', { replace: true });
        })
        .catch((error) => {
          console.error('Google OAuth error:', error);
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate, processGoogleCallback]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-odapto-orange border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected route wrapper
const ProtectedRoute = ({ children, requireAdmin = false, requirePrivileged = false }) => {
  const { user, loading, isAdmin, isPrivileged } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-odapto-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Check both React state AND localStorage token (handles race condition after login/register)
  const hasToken = !!localStorage.getItem('odapto_session_token');
  if (!user && !hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requirePrivileged && !isPrivileged) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Root route handler - shows splash on native, landing on web
const RootRoute = () => {
  if (Capacitor.isNativePlatform()) {
    return <AnimatedSplashScreen />;
  }
  return <LandingPage />;
};

// App router
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/splash" element={<AnimatedSplashScreen />} />
      <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/templates" element={
        <ProtectedRoute>
          <AppLayout><TemplatesPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/invite/accept" element={<AcceptInvitePage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout><DashboardPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace/:workspaceId"
        element={
          <ProtectedRoute>
            <AppLayout><WorkspacePage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/board/:boardId"
        element={
          <ProtectedRoute>
            <BoardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/strategy-coach"
        element={
          <ProtectedRoute>
            <AppLayout><StrategyCoachPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/strategy-coach/:sessionId"
        element={
          <ProtectedRoute>
            <AppLayout><StrategyCoachPage /></AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <IntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <UpgradePage />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/fair-use" element={<FairUsePage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/about-us" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/admin/cms"
        element={
          <ProtectedRoute requireAdmin>
            <AdminCMSPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai"
        element={
          <ProtectedRoute requireAdmin>
            <AdminAIPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/intelligence"
        element={
          <ProtectedRoute requireAdmin>
            <AdminIntelligencePage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    // Mobile: Listen for deep link callbacks (Google OAuth redirect)
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appUrlOpen', (event) => {
        const url = new URL(event.url);
        if (url.pathname === '/auth/google/callback') {
          const code = url.searchParams.get('code');
          if (code) {
            Browser.close().catch(() => {});
            // Navigate to callback route with code — handled by GoogleAuthCallback
            window.location.href = `/auth/google/callback?code=${encodeURIComponent(code)}`;
          }
        }
      });
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <DisclaimerModal />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
